import { createRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router'
import { rootRoute } from '@/ui/pages/root'
import { Header } from '@/ui/components/layout/Header'
import { Sidebar } from '@/ui/components/layout/Sidebar'
import { useAuthStore } from '@/application/stores/auth.store'
import { useSessionWatcher } from '@/application/hooks/useSessionWatcher'

// ──────────────────────────────────────────────
// Layout route — /manage
// ──────────────────────────────────────────────
export const managementLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'manage',
  beforeLoad: () => {
    const { isAuthenticated, accessToken } = useAuthStore.getState()
    if (!isAuthenticated || !accessToken) throw redirect({ to: '/login' })
    // Any authenticated user can access /manage
  },
  component: ManagementLayout,
})

export function ManagementLayout() {
  const navigate = useNavigate()
  useSessionWatcher(() => {
    void navigate({ to: '/login' })
  })

  /*
   * `min-h-screen-safe` instead of `min-h-screen`: the body pads the bottom
   * safe-area inset in a standalone PWA window (see common.css), so a `100dvh`
   * floor overflows the body by exactly the home-indicator height and leaves a
   * dead scroll gap at the end of every admin page. `min-h-screen-safe`
   * subtracts that inset back out (components.css).
   *
   * The nesting below is what makes the `sticky top-0` header in Header.tsx
   * actually work, so it is load-bearing:
   *  - A sticky element only ever travels within its own parent's box. Here the
   *    parent is the `flex-1 flex flex-col` column, which — being a stretched
   *    flex item of the row — is as tall as the whole page, growing with
   *    `<main>`. So the header has the full document to ride across. Wrapping
   *    the header in a box of its own height would pin it in place with
   *    nowhere to go (the public layout hits exactly that, and dissolves the
   *    wrapper with `display: contents`).
   *  - `<main>` must NOT become an `overflow-y-auto` scroller and nothing above
   *    it may lock the height: the DOCUMENT has to stay the scroller, because
   *    the iOS status-bar tap ("scroll to top") only ever drives the document
   *    scroller and cannot reach an inner scrolling element. The header stays
   *    in view regardless, being sticky.
   */
  return (
    <div className="min-h-screen-safe bg-background">
      <div className="flex min-h-screen-safe">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <Header />
          <main className="flex-1 p-6 bg-muted/40">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────
// Index route — /manage (redirects based on user role)
// ──────────────────────────────────────────────
export const managementIndexRoute = createRoute({
  getParentRoute: () => managementLayoutRoute,
  path: '/',
  beforeLoad: () => {
    const { user } = useAuthStore.getState()
    const isPrivileged =
      user?.role === 'streamer' || user?.role === 'moderator' || user?.role === 'admin'
    throw redirect({ to: isPrivileged ? '/manage/dashboard' : '/manage/profile' })
  },
  component: () => null,
})
